import React from 'react';
import styled from 'styled-components';

import Text from '../Text';
import theme from '../../../styles/theme';
import HTMLWrapper from '../HTMLWrapper';
import documentToReactComponents from '../../util/documentToReactComponents';

const {
	breakPoints: { screenQuery },
} = theme;

// ----------------------- THIS SUPPORTS DOWN TO 366 PX ------------------------------
// Below 576, grid-column-gaps are 30px (and columns can be 0px), so 30x11 = 330
// and 330 is bound by the 90% margin, and 330 is 90% of 366
// so anything below 366px will automatically start bleeding baybay

const Container = styled.div`
	display: ${props => (props.flex ? 'flex' : 'grid')};
	${props => (props.hideOnDesktop ? 'display: none;' : '')}
	grid-template-columns: repeat(${props => props.columns || `12`}, 1fr);
	grid-gap: ${props => props.gridGap || '60px'};
	${props => (props.cellsAreHeightOfContent ? `align-items: flex-start;` : '')}
	/* grid-auto-rows: 1fr; */
    &.margin-bottom, .grid-margin-bottom {
		margin-bottom: 60px;
	}
	&.margin-top,
	.grid-margin-top {
		margin-top: 60px;
	}
	${screenQuery.large} {
		${props => (props.hideOnLarge ? 'display: none;' : '')}
		grid-gap: 40px;
		&.margin-bottom,
		.grid-margin-bottom {
			margin-bottom: 40px;
		}
		&.margin-top,
		.grid-margin-top {
			margin-top: 40px;
		}
	}
	${screenQuery.medium} {
		${props =>
			props.showOnTablet ? 'display: ' + (props.flex ? 'flex;' : 'grid;') : ''}
		${props => (props.hideOnTablet ? 'display: none;' : '')}
	}
	${screenQuery.small} {
		${props =>
			props.showOnMobile ? 'display: ' + (props.flex ? 'flex;' : 'grid;') : ''}
		${props => (props.hideOnMobile ? 'display: none;' : '')}
        grid-gap: ${props => (props.noSmallGap ? '0px' : '30px')};
		&.margin-bottom,
		.grid-margin-bottom {
			margin-bottom: 30px;
		}
		&.margin-top,
		.grid-margin-top {
			margin-top: 30px;
		}
	}
	${screenQuery.extraMobile} {
		grid-template-columns: 1fr;
	}
`;
const GridCell = styled.div`
	grid-column-end: span ${props => props.span || 12};
	grid-column-start: ${props => props.start || 'auto'};
	${props => (props.align ? `align-self: ${props.align};` : ``)}
	${props => (props.rowStart ? `grid-row-start: ${props.rowStart};` : ``)}
    min-height: 1em;
	&.center {
		text-align: center;
		margin-left: auto;
		margin-right: auto;
	}
	&.vertical-center {
		align-self: center;
	}
	${screenQuery.large} {
		${props => (props.spanLg ? `grid-column-end: span ${props.spanLg};` : '')}
		${props => (props.inlineLg ? `grid-column-start: ${props.startLg};` : '')}
        ${props =>
			props.rowStartLg ? `grid-row-start: ${props.rowStartLg};` : ``}
        /* ${props =>
			props.inlineMd ? '' : `grid-column-start: ${props.startLg || 1};`}
        margin-left: auto;
        margin-right: auto; */
        &.centerLg {
			text-align: center;
			margin-left: auto;
			margin-right: auto;
		}
	}

	${screenQuery.medium} {
		grid-column-end: span ${props => props.spanMd || 12};
		${props =>
			props.inlineMd ? '' : `grid-column-start: ${props.startMd || 1};`}
		${props => (props.rowStartMd ? `grid-row-start: ${props.rowStartMd};` : ``)}

		&.centerMd {
			text-align: center;
			margin-left: auto;
			margin-right: auto;
		}
	}
	${screenQuery.small} {
		grid-column-end: span ${props => props.spanSm || 12};
		${props =>
			props.inlineSm ? '' : `grid-column-start: ${props.startSm || 1};`}
		${props => (props.rowStartSm ? `grid-row-start: ${props.rowStartSm};` : ``)}
        &.centerSm {
			text-align: center;
			margin-left: auto;
			margin-right: auto;
		}
	}
	${screenQuery.mobile} {
		grid-column-end: span ${props => props.spanMobile || 12};
		${props =>
			props.inlineMobile ? '' : `grid-column-start: ${props.startMobile || 1};`}
		${props =>
			props.rowStartMobile ? `grid-row-start: ${props.rowStartMobile};` : ``}
        &.centerMobile {
			text-align: center;
			margin-left: auto;
			margin-right: auto;
		}
	}
	${screenQuery.extraMobile} {
		grid-column-start: 1;
		grid-column-end: 1;
	}
	${props => (props.center ? `text-align: center;` : ``)}
`;

const HeaderCell = styled(GridCell)`
	text-align: center !important;
	max-width: 920px;
	* {
		text-align: center !important;
	}

	h2 {
		margin-bottom: 8px;
	}
`;

const Header = styled(Text)`
	margin-left: auto;
	margin-right: auto;
	${screenQuery.small} {
		width: 100%;
	}
`;

const Grid = ({ children, header, subheader, rawSubheader, ...props }) => {
	return (
		<Container className="grid-container" {...props}>
			{header && (
				<HeaderCell
					span={10}
					start={2}
					spanMd={10}
					startMd={2}
					className={`header-cell ${children ? '' : 'grid-margin-bottom'}`}
				>
					{header && <Header component="h2">{header}</Header>}
					{subheader && subheader.childMarkdownRemark ? (
						<Text component="b2">
							<HTMLWrapper html={subheader.childMarkdownRemark.html} />
						</Text>
					) : (
						<Text component="b2">{subheader}</Text>
					)}
					{rawSubheader && (
						<Text component="b2">
							{documentToReactComponents(rawSubheader)}
						</Text>
					)}
				</HeaderCell>
			)}
			{children}
		</Container>
	);
};

const Cell = ({ span, start, children, ...props }) => {
	return (
		<GridCell {...props} start={start} span={span}>
			{children}
		</GridCell>
	);
};

export { Grid, Cell };
